<template>
  <div class="grid-string grid-string-data">
    <template v-for="field in fields">
      <component
        :is="field.component"
        :key="field.slug + field.upd"
        :editable="editable"
        v-bind="field.props"
        v-on="field.events"
        @contextmenu.native="emitContext($event, field)"
      />
    </template>
  </div>
</template>

<script>
  import { mapActions } from 'vuex';
  import IconsCell from '@/components/project/list/IconsCell';
  import EmptyCell from '@/components/project/list/EmptyCell';
  import copy from '@/utils/copy';
  import factory from '@/utils/api/factory';
  import TaxInput from '@/components/common/TaxInput';
  import ListTaxContext from '@/components/project/list/contexts/Tax';
  import parseError from '@/utils/formatting/parse-error';
  import NumberCell from '@/components/common/NumberCell';
  import addSpaces, { formatMoney } from '@/utils/formatting/add-spaces';
  import RowMixin from '@/components/common/RowMixin';

  /**
   * Fast go to:
   * – {@link index_column} – first column with actions in form of icons
   * – {@link input_column} – the input of the name of the tax
   * – {@link empty_column} – empty is a cell that takes space of many cells
   * – {@link percent_column} – display a number along with its percentage
   */

  export default {
    name: 'ListTax',
    mixins: [RowMixin],

    props: {
      editable: {
        type: Boolean,
        required: true,
      },
    },

    data() {
      return {
        COMPONENTS: {
          /** @name index_column */
          index: {
            component: this.editable ? IconsCell : EmptyCell,
            props: () => ({
              id: this.data.id,
            }),
            events: (field) => ({
              add: () => this.$emit('add', this.data.order),
              options: (event) => this.emitContext(event, field),
            }),
          },

          /** @name input_column */
          input: {
            component: TaxInput,
            props: (field) => ({
              name: this.data[field.tax.slug],
              error: this.errors[field.tax.slug],
            }),
            events: (field) => ({
              change: ({ value, oldVal }) => this.changeField(value, field.tax.slug, oldVal),
            }),
          },

          /** @name empty_column */
          empty: {
            component: EmptyCell,
            props: () => ({}),
            events: () => ({}),
          },

          /** @name percent_column */
          percent: {
            component: NumberCell,
            props: (field) => ({
              label: this.data[field.tax.slug],
              currencyLabel: '%',
              error: this.errors[field.tax.slug],
              errorPosition: 'left',
              unit: {
                used: true,
                label: this.$store.state.global.currency + formatMoney(this.data[field.tax.value]),
                helper: true,
                helperCurrency: true,
                helperText: addSpaces(this.data[field.tax.value]),
              },
            }),
            events: (field) => ({
              change: ({ value, oldVal }) => this.changeField(value, field.tax.slug, oldVal),
            }),
          },
        },
      };
    },

    methods: {
      ...mapActions({
        openContext: 'context/open',
      }),

      emitContext(event, field) {
        if (this.editable) this.taxOptions(event, field);
      },

      async changeField(val, field, oldVal = null) {
        try {
          this.errors = { ...this.errors, [field]: '' };
          if (oldVal) this.data[field] = val;

          const res = await factory('additional_list_costs').updateInPath(this.data.id, {
            [field]: val,
          });

          this.data = res.data;
        } catch (e) {
          this.errors = { ...this.errors, [field]: parseError(e) };
          if (oldVal) this.data[field] = oldVal;
        }
      },

      // Context menu with all actions suitable specially for the tax row
      taxOptions(e, field = null, value = null) {
        e.preventDefault();
        const allowCopy = field.tax.copypaste;
        const allowPaste = field.tax.copypaste && this.editable;

        this.openContext({
          pageX: e.pageX,
          pageY: e.pageY,
          type: e.type,

          options: 1 + (allowCopy ? 1 : 0) + (allowPaste ? 1 : 0),
          optionsHeight: 28,
          component: ListTaxContext,
          allowCopy,
          allowPaste,

          delete: () => this.$emit('delete', this.data.id),
          copy: () => copy(value),
          paste: () => {
            navigator.clipboard.readText().then((clipText) => {
              const el = e.target;
              if (el) {
                if (el.closest('.global-position-wrap')) {
                  el
                    .closest('.global-position-wrap')
                    .querySelector('.global-position-input').value = clipText.slice(0, 200);
                } else {
                  const children = el.children || [];
                  if (children.length > 0) children[0].textContent = clipText.slice(0, 200);
                  else el.textContent = clipText.slice(0, 200);
                }
              }
            });
          },
        });
      },
    },
  };
</script>
